import { Box, LinearProgress } from "@mui/material";
import { green } from "@mui/material/colors";
import { useMemo } from "react";

import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import { getRequiredDocGroupsAndTypes } from "../../renderers/LoanRequiredDocsRenderer";
const FILL_COLOR = green[800];
const RequiredDocumentCompletedProgressBar = ({
  loan,
  loanDocuments = [],
  showOnlyProgressBar = false,
  showStatusCounts = false,
}) => {
  const stats = useMemo(() => {
    const requiredDocsByGroup = getRequiredDocGroupsAndTypes(loan);

    const allRequiredDocTypes = Object.values(requiredDocsByGroup).flat();
    const totalRequired = allRequiredDocTypes.length;

    if (totalRequired === 0) {
      return {
        total: 0,
        accepted: 0,
        newUpload: 0,
        pendingReview: 0,
        incomplete: 0,
        rejected: 0,
        progress: 0,
      };
    }

    // Count how many required doc types have at least one document in each status
    const acceptedTypes = new Set();
    const newUploadTypes = new Set();
    const pendingReviewTypes = new Set();
    const incompleteTypes = new Set();
    const rejectedTypes = new Set();

    loanDocuments.forEach((doc) => {
      const docType = doc.docType;
      const status = doc.documentStatus;

      // Only track documents that are in the required list
      if (allRequiredDocTypes.includes(docType)) {
        switch (status) {
          case "Accepted":
            acceptedTypes.add(docType);
            break;
          case "New Upload":
            newUploadTypes.add(docType);
            break;
          case "Pending Review":
            pendingReviewTypes.add(docType);
            break;
          case "Incomplete":
            incompleteTypes.add(docType);
            break;
          case "Rejected":
            rejectedTypes.add(docType);
            break;
          default:
            // Log unexpected status values for debugging
            console.warn(
              `Unexpected document status "${status}" for doc type "${docType}"`,
            );
        }
      }
    });

    const accepted = acceptedTypes.size;
    const newUpload = newUploadTypes.size;
    const pendingReview = pendingReviewTypes.size;
    const incomplete = incompleteTypes.size;
    const rejected = rejectedTypes.size;

    return {
      total: totalRequired,
      accepted,
      newUpload,
      pendingReview,
      incomplete,
      rejected,
      progress: totalRequired > 0 ? (accepted / totalRequired) * 100 : 0,
    };
  }, [loan, loanDocuments]);

  if (showOnlyProgressBar) {
    return (
      <LinearProgress
        variant="determinate"
        value={stats.progress}
        sx={{
          flexGrow: 1,
          height: 8,
          borderRadius: 4,
          backgroundColor: "#e2e8f0",
          "& .MuiLinearProgress-bar": {
            backgroundColor: FILL_COLOR,
          },
        }}
      />
    );
  }

  return (
    <Flx col g={1} sx={{ flexGrow: 1 }}>
      <Flx g={2} ac sx={{ flexGrow: 1 }}>
        <LinearProgress
          variant="determinate"
          value={stats.progress}
          sx={{
            flexGrow: 1,
            height: 8,
            borderRadius: 4,
            width: "100%",
            backgroundColor: "#e2e8f0",
            "& .MuiLinearProgress-bar": {
              backgroundColor: FILL_COLOR,
            },
          }}
        />
        <Flx ac gap={0.6} sx={{ flexShrink: 0 }}>
          <Txt secondary>
            {stats.accepted} of {stats.total}
          </Txt>
          <Txt secondary>Complete</Txt>
        </Flx>
      </Flx>

      {showStatusCounts && stats.total > 0 && (
        <Flx g={2} wrap sx={{ fontSize: "0.875rem" }}>
          {stats.accepted > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: FILL_COLOR, fontWeight: 600 }}>
                {stats.accepted}
              </Box>{" "}
              Accepted
            </Txt>
          )}
          {stats.pendingReview > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#f59e0b", fontWeight: 600 }}>
                {stats.pendingReview}
              </Box>{" "}
              Pending Review
            </Txt>
          )}
          {stats.newUpload > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#3b82f6", fontWeight: 600 }}>
                {stats.newUpload}
              </Box>{" "}
              New Upload
            </Txt>
          )}
          {stats.incomplete > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#f97316", fontWeight: 600 }}>
                {stats.incomplete}
              </Box>{" "}
              Incomplete
            </Txt>
          )}
          {stats.rejected > 0 && (
            <Txt secondary>
              <Box component="span" sx={{ color: "#ef4444", fontWeight: 600 }}>
                {stats.rejected}
              </Box>{" "}
              Rejected
            </Txt>
          )}
        </Flx>
      )}
    </Flx>
  );
};

export default RequiredDocumentCompletedProgressBar;
